import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} VariantSizeData
 * @property {string | null} colorOptionKey
 * @property {string} sizeOptionKey
 * @property {Array<{ id: number, available: boolean, option1: string | null, option2: string | null, option3: string | null }>} variants
 */

class VariantSizeList extends HTMLElement {
  /** @type {VariantSizeData | null} */
  #data = null;

  /** @type {Element | null} */
  #productCard = null;

  connectedCallback() {
    this.#parseVariantData();

    this.#productCard = this.closest('product-card');
    if (!this.#productCard) return;

    this.#productCard.addEventListener('click', this.#handleClick, true);
    this.#productCard.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.#productCard.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);

    this.refreshForCardColor();
  }

  /** Sync size strike-through with the split card color or selected swatch. */
  refreshForCardColor() {
    if (!this.#productCard) {
      this.#productCard = this.closest('product-card');
    }
    if (!this.#productCard) return;

    this.#updateAvailability(this.#getSelectedColorName(this.#productCard));
  }

  disconnectedCallback() {
    if (!this.#productCard) return;

    this.#productCard.removeEventListener('click', this.#handleClick, true);
    this.#productCard.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.#productCard.removeEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.#productCard = null;
  }

  #parseVariantData() {
    const jsonScript = this.querySelector('[data-variant-size-json]');
    if (!(jsonScript instanceof HTMLScriptElement)) return;

    try {
      this.#data = JSON.parse(jsonScript.textContent || '{}');
    } catch {
      this.#data = null;
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('.variant-image-button__button[data-color-name]');
    if (!(button instanceof HTMLElement) || !button.dataset.colorName) return;

    this.#updateAvailability(button.dataset.colorName);
  };

  #handleVariantUpdate = () => {
    if (!this.#productCard) return;

    this.#parseVariantData();
    this.#updateAvailability(this.#getSelectedColorName(this.#productCard));
  };

  #handleVariantSelected = () => {
    if (!this.#productCard) return;

    this.#updateAvailability(this.#getSelectedColorName(this.#productCard));
  };

  /**
   * @param {Element} productCard
   * @returns {string | null}
   */
  #getSelectedColorName(productCard) {
    if (productCard instanceof HTMLElement && productCard.dataset.cardColor) {
      return productCard.dataset.cardColor;
    }

    const selectedButton = productCard.querySelector(
      '.variant-image-button__button.selected[data-color-name], .variant-image-button__button[aria-pressed="true"][data-color-name]'
    );

    if (selectedButton instanceof HTMLElement && selectedButton.dataset.colorName) {
      return selectedButton.dataset.colorName;
    }

    const checkedInput = productCard.querySelector(
      'input[type="radio"]:checked[name*="Color"], input[type="radio"]:checked[name*="color"], input[type="radio"]:checked[name*="Colour"], input[type="radio"]:checked[name*="colour"]'
    );

    if (checkedInput instanceof HTMLInputElement && checkedInput.value) {
      return checkedInput.value;
    }

    const defaultColor = this.dataset.defaultColor;
    if (defaultColor) return defaultColor;

    return null;
  }

  /**
   * @param {string | null} colorName
   */
  #updateAvailability(colorName) {
    if (!this.#data) return;

    const { colorOptionKey, sizeOptionKey, variants } = this.#data;
    const items = this.querySelectorAll('[data-size]');

    for (const item of items) {
      if (!(item instanceof HTMLElement) || !item.dataset.size) continue;

      const sizeName = item.dataset.size;
      const isAvailable = variants.some((variant) => {
        const variantSize = variant[sizeOptionKey];
        if (variantSize !== sizeName) return false;

        if (!colorOptionKey || !colorName) {
          return variant.available;
        }

        return variant[colorOptionKey] === colorName && variant.available;
      });

      item.classList.toggle('variant-size-list__item--unavailable', !isAvailable);

      if (isAvailable) {
        item.removeAttribute('aria-disabled');
      } else {
        item.setAttribute('aria-disabled', 'true');
      }
    }
  }
}

if (!customElements.get('variant-size-list')) {
  customElements.define('variant-size-list', VariantSizeList);
}
